import type { z } from "zod";

import { env } from "@/config/env";
import { ApiError, apiErrorFrom, networkError } from "@/shared/api/errors";
import { notifySessionExpired, readToken } from "@/shared/api/token";

/* The one way the app talks to the Laravel API. Features call `request` from their own api.ts,
   never fetch directly, so auth, errors and response validation behave the same everywhere. */

export type QueryValue = string | number | boolean | null | undefined;
type FormValue = string | number | boolean | Blob | null | undefined;

interface RequestOptions<Schema extends z.ZodType> {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  query?: Readonly<Record<string, QueryValue | readonly QueryValue[]>>;
  /** A JSON body. */
  json?: unknown;
  /** A multipart body (files). PUT and PATCH are sent as POST with `_method`, as Laravel needs. */
  form?: Readonly<Record<string, FormValue | readonly FormValue[]>>;
  /** Validates the envelope's `data`. */
  schema: Schema;
  signal?: AbortSignal;
}

export function apiUrl(path: string, query?: RequestOptions<z.ZodType>["query"]): string {
  const url = `${env.apiUrl}/api/${path.replace(/^\//, "")}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    const values = Array.isArray(value) ? value : [value];
    for (const item of values as QueryValue[]) {
      if (item === undefined || item === null || item === "") continue;
      params.append(Array.isArray(value) ? `${key}[]` : key, String(item));
    }
  }
  const search = params.toString();
  return search ? `${url}?${search}` : url;
}

export function authHeaders(): Record<string, string> {
  const token = readToken();
  return {
    Accept: "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/** Laravel reads booleans as "1"/"0" and arrays as key[] in multipart bodies. */
export function toFormData(
  fields: Readonly<Record<string, FormValue | readonly FormValue[]>>,
): FormData {
  const body = new FormData();
  const append = (key: string, value: FormValue) => {
    if (value === undefined || value === null) return;
    if (value instanceof Blob) body.append(key, value);
    else if (typeof value === "boolean") body.append(key, value ? "1" : "0");
    else body.append(key, String(value));
  };
  for (const [key, value] of Object.entries(fields)) {
    if (Array.isArray(value)) for (const item of value as FormValue[]) append(`${key}[]`, item);
    else append(key, value as FormValue);
  }
  return body;
}

/**
 * Laravel answers in several envelopes; `data` is what the call returns:
 *   { success, message, data }   most endpoints
 *   { status, data }             pipeline statuses
 *   { data }                     API resources
 *   { message, status, token }   the password-reset flow (no data: the whole body is returned)
 */
export function unwrapEnvelope(body: unknown): unknown {
  if (typeof body === "object" && body !== null && "data" in body) {
    return body.data;
  }
  return body;
}

export async function request<Schema extends z.ZodType>(
  path: string,
  { method = "GET", query, json, form, schema, signal }: RequestOptions<Schema>,
): Promise<z.infer<Schema>> {
  const headers: Record<string, string> = authHeaders();
  let body: BodyInit | undefined;
  let sentMethod = method;
  if (form) {
    const spoof = method === "PUT" || method === "PATCH";
    body = toFormData(spoof ? { ...form, _method: method } : form);
    if (spoof) sentMethod = "POST";
  } else if (json !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(json);
  }

  let response: Response;
  try {
    response = await fetch(apiUrl(path, query), {
      method: sentMethod,
      headers,
      body: body ?? null,
      signal: signal ?? null,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    throw networkError();
  }

  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    if (response.status === 401 && readToken() !== null) notifySessionExpired();
    throw apiErrorFrom(payload, response.status, response.headers);
  }

  const parsed = schema.safeParse(unwrapEnvelope(payload));
  if (!parsed.success) {
    if (import.meta.env.DEV) {
      // Contract drift should be loud while developing, and a clean error in production.
      // eslint-disable-next-line no-console
      console.error(`[api] ${method} ${path} returned an unexpected shape`, parsed.error.issues);
    }
    throw new ApiError("INVALID_RESPONSE", "The server returned an unexpected response.", 200);
  }
  return parsed.data;
}
